/// FOURJS_START_COPYRIGHT(D,2019)
/// Property of Four Js*
/// (c) Copyright Four Js 2019, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FlowLayoutEngine", ["LayoutEngineBase"], function (context, cls) {
  /**
   * Layout engine for container that will flow items in continuous dropdown
   * Note that it should be applied to WidgetGroupBase widgets only
   * @class FlowLayoutEngine
   * @memberOf classes
   * @extends classes.LayoutEngineBase
   */
  cls.FlowLayoutEngine = context.oo.Class(cls.LayoutEngineBase, function ($super) {
    return /** @lends classes.FlowLayoutEngine.prototype */ {
      __name: "FlowLayoutEngine",

      /** @type {Number} */
      _childrenWidth: 0,

      /** @type {Number} */
      _containerWidth: 0,

      /** @type {classes.FlowDecoratorWidget} **/
      _flowDecoratorWidget: null,

      /** cached width (px) of the 3-dots flow decorator @type {?number} */
      _decoratorWidth: null,

      /**
       * Define the widget used for flowing (usually 3dots)
       * @param {classes.FlowDecoratorWidget} flowDecoratorWidget
       */
      setFlowDecoratorWidget: function (flowDecoratorWidget) {
        this._flowDecoratorWidget = flowDecoratorWidget;
      },

      /**
       * Get the widget used for flowing
       * @return {classes.FlowDecoratorWidget}
       */
      getFlowDecoratorWidget: function () {
        return this._flowDecoratorWidget;
      },

      /**
       * @inheritDoc
       */
      notifyLayoutApplied: function () {
        $super.notifyLayoutApplied.call(this);

        const throttleFunction = () => {
          this._refresh();
          this._throttle = null;
        };

        const refreshFunction = () => {
          if (this._needRefresh()) {
            // No need to refresh everytime
            if (this._throttle) {
              window.clearTimeout(this._throttle);
            }
            this._throttle = this._registerTimeout(throttleFunction.bind(this), 30);
          }
        };

        this._registerAnimationFrame(
          function () {
            this._registerAnimationFrame(refreshFunction.bind(this));
          }.bind(this)
        );
      },

      /**
       * Refresh the flowing items
       * @private
       */
      _refresh: function () {
        const containerWidth = this.getContainerWidth();
        const childrenTotalWidth = this.getChildrenWidth() + this.getFlowingChildrenWidth();
        // If the children width is more than the container width, we need to flow some children
        this._flowItems(childrenTotalWidth > containerWidth);
      },

      /**
       * Flow or unflow items
       * @param {Boolean} flow - true to flow items if necessary, false to unflow them
       * @private
       */
      _flowItems: function (flow) {
        const widget = this._widget;
        const flowDecoratorWidget = this.getFlowDecoratorWidget();
        const childrenWidth = this.getChildrenWidth();
        const containerWidth = this.getContainerWidth();

        flowDecoratorWidget.enable(flow);
        // Flow items after size calculation: add to the dropdown
        let currentChildrenWidth = this.getChildrenWidth(),
          children = null,
          currentChildWidth = 0,
          i = 0;
        if (containerWidth > childrenWidth) {
          children = flowDecoratorWidget.getFlowingChildren();
          currentChildWidth = children.length && children[i].getLayoutInformation().getRawMeasure().getWidth();
          while (i < children.length && currentChildrenWidth + currentChildWidth < containerWidth) {
            flowDecoratorWidget.flowChild(children[i], false); // unflow this child
            currentChildrenWidth += currentChildWidth;
            i++;
            currentChildWidth = children[i] && children[i].getLayoutInformation().getRawMeasure().getWidth();
          }
        } else {
          children = widget.getChildren().slice(0);
          i = children.length - 1;
          while (i >= 0 && currentChildrenWidth > containerWidth) {
            flowDecoratorWidget.flowChild(children[i], true); // flow this child
            currentChildrenWidth -= children[i].getLayoutInformation().getRawMeasure().getWidth();
            i--;
          }
        }
      },

      /**
       * Check if refresh is necessary
       * @return {boolean} true if it needs a refresh, false otherwise
       * @private
       */
      _needRefresh: function () {
        const currentChildrenWidth = this.getChildrenWidth() + this.getFlowingChildrenWidth();
        const currentContainerWidth = this.getContainerWidth();

        // No need to refresh if either of container and/or children total width did not change
        if (this._childrenWidth !== currentChildrenWidth || this._containerWidth !== currentContainerWidth) {
          // If changed, update those values
          this._childrenWidth = currentChildrenWidth;
          this._containerWidth = currentContainerWidth;
          return true;
        }
        return false;
      },

      /**
       * Calculate the total width of the children
       * @return {*}
       */
      getChildrenWidth: function () {
        // Here child might not have layout
        const reduceFunction = (prev, child) => prev + child.getLayoutInformation()?.getRawMeasure().getWidth();
        return this._widget.getChildren().reduce(reduceFunction, 0);
      },

      /**
       * Calculate the total width of the children
       * @return {*}
       */
      getFlowingChildrenWidth: function () {
        return this._flowDecoratorWidget.getFlowingChildren().reduce(function (prev, child) {
          return prev + child.getLayoutInformation().getRawMeasure().getWidth();
        }, 0);
      },

      /**
       * Get the 3-dots decorator real width
       * @return {number} width in px
       */
      getDecoratorWidth: function () {
        // The decorator isn't part of the toolbar's measured children, so its layout measure
        // is always 0: read the real width from the DOM instead, forcing it visible first
        // since a hidden element measures 0. Cached (constant size); a 0 reading (e.g. not
        // laid out yet) is not cached so the next call retries.
        if (!this._decoratorWidth) {
          const decorator = this._flowDecoratorWidget;
          const wasHidden = decorator.isHidden();
          if (wasHidden) {
            decorator.setHidden(false);
          }
          this._decoratorWidth = decorator.getElement().getBoundingClientRect().width;
          if (wasHidden) {
            decorator.setHidden(true);
          }
        }
        return this._decoratorWidth;
      },

      /**
       * Drop the cached decorator width. Call when the toolbar font-size (and thus the
       * 3-dots em-based width) may have changed: button size, resize, theme/font reload.
       */
      invalidateDecoratorWidth: function () {
        this._decoratorWidth = null;
      },

      /**
       * Calculate the container Width, take the 3dots icon into account
       * @return {number}
       */
      getContainerWidth: function () {
        // Modal case: get the previously set toolbar width
        const formLayout = this._widget.getFormWidget().getLayoutInformation();
        const window = this._widget.getWindowWidget();
        const allocatedWidth = window?.isModal ? formLayout.getToolbarAllocatedWidth() : formLayout.getRawMeasure().getWidth();
        return Math.ceil(allocatedWidth - this.getDecoratorWidth());
      }
    };
  });
});
